'use client';

import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import type { Brand } from '@/domainLogic/index.ts';
import {
  getServicePreviewLabel,
  useExtendedSortedHotelServices,
  FACILITY_ICONS,
} from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';
import { useTranslation } from '@/app/components/translations/Translation';
import { trackAmenitiesSummaryClick } from '@/app/components/hotel-detail/trackInteractionEvents';
import customCss from '@/app/components/hotel-detail/css/customCss';
import ScrollToIdLink from '@/app/components/hotel-detail/ScrollToIdLink';
import { HotelFacilitiesDialogContent } from '@/app/components/hotel-detail/HotelPageFacilitiesSection';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import BasicModalHeader from '@/app/components/search/components/Modal/LayoutElements/BasicModalHeader';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { createDetailPageUserActionPayload } from '@/app/common/starTrack/events/detailPageUserAction.ts';
import { Icon } from '@/app/common/icons/Icon';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';

type HeroFacilitiesSummaryProps = React.HTMLAttributes<HTMLElement> & {
  openFacilitiesModal?: boolean;
};

// #region styled components
const Title = styled(Text).attrs({ variant: 'Title5' })`
  display: none;
  margin-bottom: 8px;
  @media ${customCss.breakpoints.sm} {
    display: block;
  }
`;

const Amenity = styled.li`
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  ${({ theme }) => theme.kallisto.mixins.font('Caption')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
  }

  @media ${customCss.breakpoints.sm} {
    border: 0;
    padding: 4px 0;
    flex-direction: row;
    ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
    cursor: default;
    gap: 8px;

    img {
      width: 20px;
      height: 20px;
    }
  }
`;

export const AmenitiesList = styled.ul`
  display: flex;
  gap: 8px;
  padding: 0 16px;
  margin: 0 -16px;
  overflow-x: auto;
  scrollbar-width: none;

  @media ${customCss.breakpoints.sm} {
    display: block;
    column-count: 2;
    gap: 4px 24px;
  }
`;

const AmenityText = styled.span`
  white-space: nowrap;
  @media ${customCss.breakpoints.sm} {
    white-space: normal;
  }
`;
// #endregion styled components

const MAX_HOTEL_SERVICES_TO_SHOW = 10;

export function HeroFacilitiesSummary({ openFacilitiesModal, ...props }: HeroFacilitiesSummaryProps) {
  const { t } = useTranslation();
  const { staticData, commonData } = usePageWrapperData();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { sendCuriosityClickEvent } = useCuriosity();
  const [isOpen, setOpen] = useState<boolean | undefined>(undefined);
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const selectedServices = useExtendedSortedHotelServices(hotelStaticData?.services);
  let amenities = hotelStaticData?.amenities;
  const { starTrack } = useCommonClient();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const initialParams = useInitialParams();
  const { productDetailKey } = initialParams;

  if (!(amenities && amenities.length > 0)) {
    if (!(selectedServices && selectedServices.length > 0)) return null;
    amenities = selectedServices.map((service) => ({
      label: getServicePreviewLabel(service, t),
      icon: FACILITY_ICONS[service.icon],
    }));
  }

  const hotelAmenities = isMobile ? amenities : amenities.slice(0, MAX_HOTEL_SERVICES_TO_SHOW);

  const handleClick = () => {
    starTrack?.detailPageUserAction(
      createDetailPageUserActionPayload({
        searchId: searchId || '',
        productDetailKey,
        action: 'SERVICES',
      })
    );
    sendCuriosityClickEvent({
      event_name: 'click_view_all_facilities',
    });
    trackAmenitiesSummaryClick();
    setOpen(true);
  };

  const showModal = selectedServices && (selectedServices.length > 1 || selectedServices[0]?.services.length > 1);

  const AmenitiesListComponent = AmenitiesList;
  const AmenityComponent = Amenity;

  return (
    <div {...props}>
      <Title>{t('vacation.select.details.product.quickinfo.facilities.title')}</Title>
      <AmenitiesListComponent>
        {hotelAmenities.map((amenity) => (
          <AmenityComponent key={amenity.label} onClick={handleClick}>
            {amenity.icon && <Icon {...amenity.icon} size={24} />}
            <AmenityText>{amenity.label}</AmenityText>
          </AmenityComponent>
        ))}
      </AmenitiesListComponent>

      {!isMobile ? (
        openFacilitiesModal ? (
          <button
            onClick={handleClick}
            css={css`
              display: block;
              padding: 4px 0;
              margin-top: 2px;
              background: none;
              border: none;
              cursor: pointer;
              text-align: left;
              ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
            `}
          >
            {t('vacation.select.details.product.quickinfo.facilities.link')}
          </button>
        ) : (
          <ScrollToIdLink
            targetId="facilities"
            css={css`
              display: block;
              padding: 4px 0;
              margin-top: 2px;
              ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
            `}
            onClick={() => {
              starTrack?.detailPageUserAction(
                createDetailPageUserActionPayload({
                  searchId: searchId || '',
                  productDetailKey,
                  action: 'SERVICES',
                })
              );
              sendCuriosityClickEvent({
                event_name: 'click_view_all_facilities',
              });
              trackAmenitiesSummaryClick();
            }}
          >
            {t('vacation.select.details.product.quickinfo.facilities.link')}
          </ScrollToIdLink>
        )
      ) : undefined}

      {isOpen !== undefined && showModal ? (
        <OpenXModal
          isOpen={isOpen}
          isMobile={true}
          brand={staticData.domainValues?.brand as Brand}
          onClose={() => setOpen(false)}
          onCloseByBrowserHistory={() => setOpen(false)}
          onOpenByBrowserHistory={() => setOpen(true)}
          fullScreen={false}
          id="hotel-facilities-modal"
          aria-label={t('vacation.select.facilities.title')}
          bodyBackgroundColor="backgroundPage"
          header={{
            sticky: true,
            element: (
              <BasicModalHeader handleClose={() => setOpen(false)}>
                {t('vacation.select.facilities.title')}
              </BasicModalHeader>
            ),
          }}
        >
          <HotelFacilitiesDialogContent services={selectedServices} />
        </OpenXModal>
      ) : undefined}
    </div>
  );
}
